import { PaymentMethodCode } from '../../../../shared/types/payment-method.enum';
import { PhoneValidator } from '../../../../shared/validators/phone.validator';

/** Membership collect-payment methods (Cash is never offered for monthly membership). */
export type VendorMembershipPaymentMethod =
  | PaymentMethodCode.TapToPay
  | PaymentMethodCode.Card
  | PaymentMethodCode.Terminal
  | PaymentMethodCode.Link;

export const VENDOR_MEMBERSHIP_PAYMENT_METHODS: readonly VendorMembershipPaymentMethod[] = [
  PaymentMethodCode.TapToPay,
  PaymentMethodCode.Card,
  PaymentMethodCode.Terminal,
  PaymentMethodCode.Link,
] as const;

export interface VendorAddPaymentMethodOptions {
  isMobileOrTablet: boolean;
  showTerminalPaymentMethod: boolean;
  isTerminalPaymentMethodEnabled: boolean;
}

export interface VendorPaymentCollectionFlags {
  paymentRequired?: boolean | null;
  hasPayment?: boolean | null;
  customerSource?: string | null;
}

/**
 * Same Tap-to-Pay / Terminal enablement rules as payment-checkout for memberships.
 * Cash is excluded (monthly membership never allows cash).
 * Payment Link stays visible but is disabled for vendor membership collection.
 */
export function isVendorAddPaymentMethodEnabled(
  method: VendorMembershipPaymentMethod,
  options: VendorAddPaymentMethodOptions
): boolean {
  switch (method) {
    case PaymentMethodCode.TapToPay:
      return Boolean(options.isMobileOrTablet);
    case PaymentMethodCode.Terminal:
      return (
        Boolean(options.showTerminalPaymentMethod) &&
        Boolean(options.isTerminalPaymentMethodEnabled)
      );
    case PaymentMethodCode.Card:
      return true;
    case PaymentMethodCode.Link:
      return false;
    default:
      return false;
  }
}

export function isVendorAddPaymentMethodVisible(
  method: VendorMembershipPaymentMethod,
  options: VendorAddPaymentMethodOptions
): boolean {
  if (method === PaymentMethodCode.Terminal) {
    return Boolean(options.showTerminalPaymentMethod);
  }
  return true;
}

/** Default method matches checkout subscription defaults: TTP on mobile, else Card. */
export function getDefaultVendorAddPaymentMethod(
  options: VendorAddPaymentMethodOptions
): VendorMembershipPaymentMethod {
  if (isVendorAddPaymentMethodEnabled(PaymentMethodCode.TapToPay, options)) {
    return PaymentMethodCode.TapToPay;
  }
  return PaymentMethodCode.Card;
}

/**
 * Show Collect Payment / Add Payment when redeem/SSE says payment is still needed.
 * Prefer explicit API flags; fall back to vendor source when flags are absent.
 */
export function shouldShowVendorAddPayment(flags: VendorPaymentCollectionFlags): boolean {
  if (typeof flags.paymentRequired === 'boolean') {
    return flags.paymentRequired;
  }
  if (typeof flags.hasPayment === 'boolean') {
    return !flags.hasPayment;
  }
  const source = String(flags.customerSource ?? '')
    .trim()
    .toLowerCase();
  return Boolean(source) && source !== 'way' && source !== 'lpr' && source !== 'shop';
}

/**
 * Phone rules match payment-checkout / monthly membership:
 * - Payment Link requires a valid US number
 * - Other methods: empty OK; if entered must be valid US (no country code)
 */
export function isVendorAddPaymentPhoneValid(
  phone: string | null | undefined,
  method: VendorMembershipPaymentMethod
): boolean {
  const trimmed = String(phone ?? '').trim();
  if (!trimmed) {
    return method !== PaymentMethodCode.Link;
  }
  return PhoneValidator.isValidUsaPhone(trimmed);
}

/** Prefill for phone inputs: national US format, strip leading +1/1 when possible. */
export function toVendorUsaPhoneInputValue(phone: string | null | undefined): string {
  const raw = String(phone ?? '').trim();
  if (!raw) {
    return '';
  }
  const digits = PhoneValidator.stripFormatting(raw);
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length === 10) {
    return PhoneValidator.formatUsaPhone(national);
  }
  return raw;
}

export function canConfirmVendorAddPayment(
  method: VendorMembershipPaymentMethod,
  options: VendorAddPaymentMethodOptions,
  isBusy: boolean,
  phone: string | null | undefined = '',
  isCardFormComplete = true
): boolean {
  if (isBusy) {
    return false;
  }
  if (!isVendorAddPaymentMethodEnabled(method, options)) {
    return false;
  }
  if (method === PaymentMethodCode.Card && !isCardFormComplete) {
    return false;
  }
  return isVendorAddPaymentPhoneValid(phone, method);
}

/**
 * Card is collected inline on the Active Membership modal (Stripe fields + Confirm).
 * Terminal still needs the checkout reader UI after prepare.
 * Link / Tap to Pay continue into existing checkout handlers after prepare.
 */
export function shouldProcessVendorMembershipCardInline(
  method: VendorMembershipPaymentMethod
): boolean {
  return method === PaymentMethodCode.Card;
}

/**
 * After Confirm Payment, only Tap to Pay / Payment Link auto-continue into checkout handlers.
 * Card stays on this modal; Terminal opens checkout for reader selection.
 */
export function shouldAutoContinueVendorMembershipCheckout(
  method: VendorMembershipPaymentMethod
): boolean {
  return method === PaymentMethodCode.TapToPay || method === PaymentMethodCode.Link;
}
